import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  ApiError,
  authApi,
  onSessionEnded,
  type GrantedStore,
  type SessionUser,
} from '../lib/api';

type Status = 'loading' | 'anonymous' | 'awaiting-mfa' | 'authenticated';

interface AuthState {
  status: Status;
  user: SessionUser | null;
  stores: GrantedStore[];
  activeStore: GrantedStore | null;
  /** Anonymous because the server ended the session, not because of Sign out. */
  sessionEnded: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  submitMfa: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  selectStore: (storeId: string) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Remembers the last store, so a reload does not always land on the first one. */
const ACTIVE_STORE_KEY = 'qs-active-store';

function readRememberedStore(): string | null {
  try {
    return localStorage.getItem(ACTIVE_STORE_KEY);
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const queryClient = useQueryClient();
  const [state, setState] = useState<AuthState>({
    status: 'loading',
    user: null,
    stores: [],
    activeStore: null,
    sessionEnded: false,
  });

  // The session-ended listener is registered once, so it reads the status from
  // here rather than from the render it was created in.
  const statusRef = useRef<Status>('loading');
  useEffect(() => {
    statusRef.current = state.status;
  }, [state.status]);

  const applySession = useCallback((user: SessionUser, stores: GrantedStore[]) => {
    const remembered = readRememberedStore();
    // The remembered ID is validated against what the server just sent. A grant
    // removed since the last visit must not resurrect from localStorage.
    const activeStore =
      stores.find((store) => store.storeId === remembered) ?? stores[0] ?? null;

    setState({ status: 'authenticated', user, stores, activeStore, sessionEnded: false });
  }, []);

  const forgetSession = useCallback(
    (sessionEnded: boolean) => {
      // Every cached figure belongs to a store this person may no longer be able
      // to see. Clearing is not tidiness — a cache that outlives the session is
      // how the next user of a shared device sees the previous one's takings.
      queryClient.clear();
      try {
        localStorage.removeItem(ACTIVE_STORE_KEY);
      } catch {
        // Private browsing; nothing was persisted to begin with.
      }

      setState({ status: 'anonymous', user: null, stores: [], activeStore: null, sessionEnded });
    },
    [queryClient],
  );

  useEffect(() => {
    onSessionEnded(() => {
      // Only a live session can end. Before sign-in completes, a 401 is a wrong
      // password or code, and the sign-in form reports that itself.
      if (statusRef.current !== 'authenticated') return;
      // A page fires several requests at once and every one comes back 401;
      // the first is the news.
      statusRef.current = 'anonymous';
      forgetSession(true);
    });
    return () => onSessionEnded(null);
  }, [forgetSession]);

  // Bootstrap: the cookie is httpOnly, so the only way to know whether we are
  // signed in is to ask.
  useEffect(() => {
    let cancelled = false;

    authApi
      .me()
      .then(({ user, stores }) => {
        if (!cancelled) applySession(user, stores);
      })
      .catch(() => {
        if (!cancelled) {
          setState({
            status: 'anonymous',
            user: null,
            stores: [],
            activeStore: null,
            sessionEnded: false,
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [applySession]);

  const login = useCallback(
    async (email: string, password: string) => {
      const { mfaRequired } = await authApi.login(email, password);

      if (mfaRequired) {
        setState((current) => ({ ...current, status: 'awaiting-mfa' }));
        return;
      }

      const { user, stores } = await authApi.me();
      applySession(user, stores);
    },
    [applySession],
  );

  const submitMfa = useCallback(
    async (code: string) => {
      await authApi.submitMfa(code);
      const { user, stores } = await authApi.me();
      applySession(user, stores);
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    // Requests still in flight come back 401 once the server has ended the
    // session. That is this sign-out, not a session ending under the reader.
    statusRef.current = 'anonymous';

    try {
      await authApi.logout();
    } catch (error) {
      // An already-dead session still logs out locally; there is nothing useful
      // to do with the error and staying "logged in" would be worse.
      if (!(error instanceof ApiError)) throw error;
    }

    forgetSession(false);
  }, [forgetSession]);

  const selectStore = useCallback(
    (storeId: string) => {
      setState((current) => {
        const next = current.stores.find((store) => store.storeId === storeId);
        if (!next || next.storeId === current.activeStore?.storeId) return current;

        // Drop everything cached for the previous store before switching. Query
        // keys are store-scoped, but clearing outright removes any chance that a
        // key missing its store segment leaks across the boundary.
        queryClient.clear();

        try {
          localStorage.setItem(ACTIVE_STORE_KEY, storeId);
        } catch {
          // Preference only.
        }

        return { ...current, activeStore: next };
      });
    },
    [queryClient],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, submitMfa, logout, selectStore }),
    [state, login, submitMfa, logout, selectStore],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
